/**
 * Réparation d'un fichier IAM / Terraform édité à la main et devenu invalide.
 *
 * Deux étages de correction sont appliqués :
 *  1. Réparation *syntaxique* : un nettoyage tolérant qui accepte du JSON « sale »
 *     là où `JSON.parse` échoue (commentaires, virgules traînantes, guillemets
 *     simples ou typographiques, clés non quotées, constantes True/False/None...).
 *  2. Réparation *structurelle* : normalisation propre au format détecté
 *     automatiquement — policy IAM AWS (`{ Version, Statement }`) ou statements
 *     Terraform `aws_iam_policy_document` (tableau).
 *
 * `repair` retourne le fichier corrigé ainsi qu'un rapport des corrections
 * appliquées (agrégées par type).
 */

export type RepairFormat = 'iam' | 'terraform';

export interface RepairSummaryItem {
  message: string;
  count: number;
}

export interface RepairResult {
  output: string;
  format: RepairFormat;
  fixes: RepairSummaryItem[];
}

const SMART_DQUOTE = /[\u201C\u201D]/;
const SMART_SQUOTE = /[\u2018\u2019]/;

/**
 * Espaces Unicode que JSON n'accepte pas (insécables, fines, cadratins, tab
 * vertical, form feed, NEL...) : souvent introduits par un copier-coller depuis
 * le web ou un traitement de texte, et invisibles à l'œil.
 */
const isUnicodeSpace = (code: number): boolean =>
  code === 0x00a0 ||
  code === 0x1680 ||
  (code >= 0x2000 && code <= 0x200a) ||
  code === 0x202f ||
  code === 0x205f ||
  code === 0x3000 ||
  code === 0x0085 ||
  code === 0x000b ||
  code === 0x000c;

/** Caractères de largeur nulle (zero-width space/joiner, BOM en milieu de flux). */
const isZeroWidth = (code: number): boolean =>
  code === 0x200b ||
  code === 0x200c ||
  code === 0x200d ||
  code === 0x2060 ||
  code === 0xfeff;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Étage 1a : scanner caractère par caractère, conscient des chaînes, qui
 * supprime les commentaires et normalise les guillemets (simples et
 * typographiques) en guillemets doubles valides.
 */
const sanitizeQuotesAndComments = (input: string, fixes: string[]): string => {
  let out = '';
  let mode: 'normal' | 'double' | 'single' | 'line' | 'block' = 'normal';
  let usedComments = false;
  let usedSingle = false;
  let usedSmart = false;
  let usedWeirdSpace = false;
  let usedZeroWidth = false;
  const n = input.length;

  for (let i = 0; i < n; i += 1) {
    const c = input[i];
    const next = i + 1 < n ? input[i + 1] : '';

    if (mode === 'normal') {
      if (c === '/' && next === '/') {
        mode = 'line';
        usedComments = true;
        i += 1;
      } else if (c === '/' && next === '*') {
        mode = 'block';
        usedComments = true;
        i += 1;
      } else if (c === '"') {
        mode = 'double';
        out += '"';
      } else if (SMART_DQUOTE.test(c)) {
        mode = 'double';
        usedSmart = true;
        out += '"';
      } else if (c === "'") {
        mode = 'single';
        usedSingle = true;
        out += '"';
      } else if (SMART_SQUOTE.test(c)) {
        mode = 'single';
        usedSingle = true;
        usedSmart = true;
        out += '"';
      } else if (isUnicodeSpace(c.charCodeAt(0))) {
        usedWeirdSpace = true;
        out += ' ';
      } else if (isZeroWidth(c.charCodeAt(0))) {
        usedZeroWidth = true;
      } else {
        out += c;
      }
      continue;
    }

    if (mode === 'double') {
      if (c === '\\') {
        out += c + next;
        i += 1;
      } else if (c === '"') {
        mode = 'normal';
        out += '"';
      } else if (SMART_DQUOTE.test(c)) {
        mode = 'normal';
        usedSmart = true;
        out += '"';
      } else {
        out += c;
      }
      continue;
    }

    if (mode === 'single') {
      if (c === '\\') {
        if (next === "'") {
          out += "'";
        } else {
          out += c + next;
        }
        i += 1;
      } else if (c === "'") {
        mode = 'normal';
        out += '"';
      } else if (SMART_SQUOTE.test(c)) {
        mode = 'normal';
        usedSmart = true;
        out += '"';
      } else if (c === '"') {
        out += '\\"';
      } else {
        out += c;
      }
      continue;
    }

    if (mode === 'line') {
      if (c === '\n') {
        mode = 'normal';
        out += '\n';
      }
      continue;
    }

    // mode === 'block'
    if (c === '*' && next === '/') {
      mode = 'normal';
      i += 1;
    }
  }

  if (usedComments) {
    fixes.push('Commentaires supprimés (le JSON ne les autorise pas).');
  }
  if (usedSingle) {
    fixes.push('Guillemets simples convertis en guillemets doubles.');
  }
  if (usedSmart) {
    fixes.push(
      'Guillemets typographiques (« smart quotes ») remplacés par des guillemets droits.',
    );
  }
  if (usedWeirdSpace) {
    fixes.push(
      'Espaces Unicode non standard (insécables, fines...) remplacés par des espaces normaux.',
    );
  }
  if (usedZeroWidth) {
    fixes.push('Caractères de largeur nulle supprimés.');
  }
  return out;
};

interface Segment {
  type: 'code' | 'string';
  value: string;
}

/** Découpe un texte JSON en segments chaîne / hors-chaîne (conscient des échappements). */
const splitSegments = (text: string): Segment[] => {
  const segments: Segment[] = [];
  let mode: 'normal' | 'string' = 'normal';
  let buf = '';

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (mode === 'normal') {
      if (c === '"') {
        if (buf) {
          segments.push({ type: 'code', value: buf });
        }
        buf = '"';
        mode = 'string';
      } else {
        buf += c;
      }
    } else {
      buf += c;
      if (c === '\\') {
        buf += text[i + 1] ?? '';
        i += 1;
      } else if (c === '"') {
        segments.push({ type: 'string', value: buf });
        buf = '';
        mode = 'normal';
      }
    }
  }
  if (buf) {
    segments.push({ type: mode === 'string' ? 'string' : 'code', value: buf });
  }
  return segments;
};

/**
 * Étage 1b : corrections appliquées uniquement hors des chaînes (virgules
 * traînantes, clés non quotées, constantes non-JSON).
 */
const sanitizeStructureSyntax = (text: string, fixes: string[]): string => {
  let trailing = false;
  let unquoted = false;
  let consts = false;

  const out = splitSegments(text)
    .map(segment => {
      if (segment.type !== 'code') {
        return segment.value;
      }
      let value = segment.value.replace(/,(\s*[}\]])/g, (_m, tail: string) => {
        trailing = true;
        return tail;
      });
      value = value.replace(
        /([{,]\s*)([A-Za-z_$][A-Za-z0-9_$-]*)(\s*:)/g,
        (_m, head: string, key: string, tail: string) => {
          unquoted = true;
          return `${head}"${key}"${tail}`;
        },
      );
      value = value.replace(/\b(True|False|None)\b/g, (match: string) => {
        consts = true;
        if (match === 'True') {
          return 'true';
        }
        return match === 'False' ? 'false' : 'null';
      });
      return value;
    })
    .join('');

  if (trailing) {
    fixes.push('Virgules traînantes supprimées.');
  }
  if (unquoted) {
    fixes.push('Clés non quotées entourées de guillemets.');
  }
  if (consts) {
    fixes.push('Constantes True/False/None converties en true/false/null.');
  }
  return out;
};

const capitalizeEffect = (effect: unknown): string => {
  if (typeof effect !== 'string' || !effect) {
    return 'Allow';
  }
  const lower = effect.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const dedupe = (values: unknown[]): unknown[] => {
  if (values.every(v => typeof v === 'string')) {
    return Array.from(new Set(values));
  }
  return values;
};

/** Réordonne les clés d'un objet selon `order`, puis conserve les inconnues. */
const orderKeys = (
  obj: Record<string, unknown>,
  order: string[],
): Record<string, unknown> => {
  const ordered: Record<string, unknown> = {};
  for (const key of order) {
    if (obj[key] !== undefined) {
      ordered[key] = obj[key];
    }
  }
  for (const key of Object.keys(obj)) {
    if (!(key in ordered)) {
      ordered[key] = obj[key];
    }
  }
  return ordered;
};

const normalizeKey = (
  key: string,
  map: Record<string, string>,
): string | undefined => map[key.toLowerCase().replace(/_/g, '')];

const IAM_TOP_KEYS: Record<string, string> = {
  version: 'Version',
  id: 'Id',
  statement: 'Statement',
};

const IAM_STMT_KEYS: Record<string, string> = {
  sid: 'Sid',
  effect: 'Effect',
  action: 'Action',
  notaction: 'NotAction',
  resource: 'Resource',
  notresource: 'NotResource',
  principal: 'Principal',
  notprincipal: 'NotPrincipal',
  condition: 'Condition',
};

const TF_STMT_KEYS: Record<string, string> = {
  sid: 'sid',
  effect: 'effect',
  action: 'actions',
  actions: 'actions',
  notaction: 'not_actions',
  notactions: 'not_actions',
  resource: 'resources',
  resources: 'resources',
  notresource: 'not_resources',
  notresources: 'not_resources',
  principal: 'principals',
  principals: 'principals',
  notprincipal: 'not_principals',
  notprincipals: 'not_principals',
  condition: 'condition',
  conditions: 'condition',
};

const renameKeys = (
  obj: Record<string, unknown>,
  map: Record<string, string>,
  fixes: string[],
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const canonical = normalizeKey(key, map) ?? key;
    if (canonical !== key) {
      fixes.push(`Clé « ${key} » renommée en « ${canonical} ».`);
    }
    out[canonical] = value;
  }
  return out;
};

const repairIamStatement = (
  statement: unknown,
  fixes: string[],
): Record<string, unknown> | unknown => {
  if (!isObject(statement)) {
    return statement;
  }
  const out = renameKeys(statement, IAM_STMT_KEYS, fixes);

  if (out.Effect === undefined) {
    out.Effect = 'Allow';
    fixes.push("'Effect' manquant ajouté (valeur par défaut « Allow »).");
  } else {
    const cap = capitalizeEffect(out.Effect);
    if (cap !== out.Effect) {
      fixes.push(`Effect « ${String(out.Effect)} » normalisé en « ${cap} ».`);
      out.Effect = cap;
    }
  }

  for (const key of ['Action', 'NotAction', 'Resource', 'NotResource']) {
    const value = out[key];
    if (Array.isArray(value)) {
      const cleaned = dedupe(value);
      if (cleaned.length !== value.length) {
        fixes.push(`Doublons supprimés dans ${key}.`);
      }
      out[key] = cleaned;
    }
  }

  return orderKeys(out, [
    'Sid',
    'Effect',
    'Principal',
    'NotPrincipal',
    'Action',
    'NotAction',
    'Resource',
    'NotResource',
    'Condition',
  ]);
};

const repairIam = (
  data: Record<string, unknown>,
  fixes: string[],
): Record<string, unknown> => {
  let source = data;
  const hasStatement = Object.keys(data).some(
    k => k.toLowerCase() === 'statement',
  );
  if (!hasStatement) {
    fixes.push(
      "Aucune clé « Statement » trouvée : contenu encapsulé dans un tableau Statement.",
    );
    source = { Statement: data };
  }

  const top = renameKeys(source, IAM_TOP_KEYS, fixes);

  if (top.Version === undefined) {
    top.Version = '2012-10-17';
    fixes.push("« Version » manquante ajoutée (« 2012-10-17 »).");
  }

  let statements = top.Statement;
  if (!Array.isArray(statements)) {
    fixes.push('« Statement » normalisé en tableau.');
    statements = [statements];
  }
  top.Statement = (statements as unknown[]).map(s =>
    repairIamStatement(s, fixes),
  );

  return orderKeys(top, ['Version', 'Id', 'Statement']);
};

const repairTfCondition = (
  condition: unknown,
  fixes: string[],
): unknown => {
  if (!isObject(condition)) {
    return condition;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(condition)) {
    const lower = key.toLowerCase();
    if (lower === 'test' || lower === 'variable' || lower === 'values') {
      out[lower] = value;
    } else {
      out[key] = value;
    }
  }
  if (out.values !== undefined && !Array.isArray(out.values)) {
    out.values = [out.values];
    fixes.push('condition.values converti en tableau.');
  }
  return orderKeys(out, ['test', 'variable', 'values']);
};

const repairTfStatement = (
  statement: unknown,
  fixes: string[],
): Record<string, unknown> | unknown => {
  if (!isObject(statement)) {
    return statement;
  }
  const out = renameKeys(statement, TF_STMT_KEYS, fixes);

  if (out.effect === undefined) {
    out.effect = 'Allow';
    fixes.push("'effect' manquant ajouté (valeur par défaut « Allow »).");
  } else {
    const cap = capitalizeEffect(out.effect);
    if (cap !== out.effect) {
      fixes.push(`effect « ${String(out.effect)} » normalisé en « ${cap} ».`);
      out.effect = cap;
    }
  }

  for (const key of ['actions', 'not_actions', 'resources', 'not_resources']) {
    const value = out[key];
    if (value !== undefined && !Array.isArray(value)) {
      out[key] = [value];
      fixes.push(`${key} converti en tableau (Terraform attend une liste).`);
    }
    const arr = out[key];
    if (Array.isArray(arr)) {
      const cleaned = dedupe(arr);
      if (cleaned.length !== arr.length) {
        fixes.push(`Doublons supprimés dans ${key}.`);
      }
      out[key] = cleaned;
    }
  }

  if (Array.isArray(out.condition)) {
    out.condition = (out.condition as unknown[]).map(c =>
      repairTfCondition(c, fixes),
    );
  }

  return orderKeys(out, [
    'sid',
    'effect',
    'principals',
    'not_principals',
    'actions',
    'not_actions',
    'resources',
    'not_resources',
    'condition',
  ]);
};

const repairTerraform = (data: unknown, fixes: string[]): unknown[] => {
  let arr = data;
  if (!Array.isArray(arr)) {
    fixes.push('Statements Terraform normalisés en tableau.');
    arr = [arr];
  }
  return (arr as unknown[]).map(s => repairTfStatement(s, fixes));
};

const summarize = (list: string[]): RepairSummaryItem[] => {
  const counts = new Map<string, number>();
  for (const message of list) {
    counts.set(message, (counts.get(message) ?? 0) + 1);
  }
  return Array.from(counts, ([message, count]) => ({ message, count }));
};

/**
 * Répare une entrée (chaîne) et retourne le fichier corrigé + le rapport.
 * Lève une `Error` si le JSON reste irrécupérable après nettoyage.
 */
export const repair = (input: string): RepairResult => {
  const trimmed = input.trim();
  if (!trimmed) {
    return { output: '', format: 'iam', fixes: [] };
  }

  const fixes: string[] = [];

  let text = trimmed;
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
    fixes.push('Marqueur BOM en début de fichier supprimé.');
  }

  text = sanitizeQuotesAndComments(text, fixes);
  text = sanitizeStructureSyntax(text, fixes);

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error(
      `Impossible de réparer automatiquement le JSON : ${
        e instanceof Error ? e.message : String(e)
      }`,
    );
  }

  const format: RepairFormat = Array.isArray(parsed) ? 'terraform' : 'iam';

  let repaired: unknown;
  if (format === 'terraform') {
    repaired = repairTerraform(parsed, fixes);
  } else if (isObject(parsed)) {
    repaired = repairIam(parsed, fixes);
  } else {
    throw new Error(
      'Contenu non reconnu : ni policy IAM (objet) ni statements Terraform (tableau).',
    );
  }

  return {
    output: JSON.stringify(repaired, null, 2),
    format,
    fixes: summarize(fixes),
  };
};
